/**
 * Getypeerde toegang tot de berichtenlaag: het log, de automatiseringen en de
 * sjablonen.
 *
 * Eén ding is hier geen implementatiedetail. **Een sjabloon wordt in zijn geheel
 * vervangen, en teruggezet door hem weg te halen.** Er is geen "leeg opslaan": een
 * leeg sjabloon zou een leeg bericht opleveren, en dat is nooit wat iemand bedoelt.
 * Wie terug wil naar de platformtekst, verwijdert zijn eigen versie — dan komt de
 * standaard weer bovendrijven.
 */
import { call, isRecord, readCount, readFlag, readText } from './http'
import type { Failure, Result } from './http'
import type { RequestOptions } from './api-client'

export type MessagingFailure = Failure
export type MessagingResult<T> = Result<T>

/** Exact de CHECK op `automation_rules.trigger` (migratie 0032). */
export const AUTOMATION_TRIGGERS = [
  'booking_created',
  'booking_confirmed',
  'booking_declined',
  'booking_cancelled',
  'before_arrival',
  'before_departure',
  'after_departure',
] as const
export type AutomationTrigger = (typeof AUTOMATION_TRIGGERS)[number]

/** Alleen bij deze drie is een verschuiving in dagen betekenisvol. */
export const SCHEDULED_TRIGGERS: readonly AutomationTrigger[] = [
  'before_arrival',
  'before_departure',
  'after_departure',
]

export const RECIPIENTS = ['guest', 'owner'] as const
export type Recipient = (typeof RECIPIENTS)[number]

export const MESSAGE_STATUSES = ['pending', 'sent', 'failed', 'cancelled'] as const
export type MessageStatus = (typeof MESSAGE_STATUSES)[number]

export interface AutomationRule {
  readonly id: string
  readonly trigger: AutomationTrigger
  readonly recipient: Recipient
  readonly templateKey: string
  readonly offsetDays: number
  readonly sendAtHour: number
  readonly enabled: boolean
}

export interface MessageTemplate {
  readonly id: string
  readonly templateKey: string
  readonly locale: string
  readonly subject: string
  readonly body: string
  /** Waar of niet: dit is de eigen versie en niet de platformstandaard. */
  readonly isOverride: boolean
  /**
   * De plaatshouders die in dít sjabloon mogen.
   *
   * Komt van de API en wordt hier niet nagebouwd: de lijst volgt uit de code die de
   * gegevens samenstelt, en een tweede kopie zou een plaatshouder kunnen aanbieden
   * die niemand invult.
   */
  readonly placeholders: readonly string[]
}

export interface LoggedMessage {
  readonly id: string
  readonly stayId: string | null
  readonly trigger: string
  readonly recipient: Recipient
  readonly locale: string
  readonly toAddress: string
  readonly subject: string
  readonly body: string
  readonly status: MessageStatus
  readonly attempts: number
  readonly lastError: string
  readonly scheduledFor: string
  readonly sentAt: string | null
}

function isOneOf<T extends string>(value: unknown, known: readonly T[]): value is T {
  return typeof value === 'string' && (known as readonly string[]).includes(value)
}

function readMessage(value: unknown): LoggedMessage | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const status = value['status']
  const recipient = value['recipient']
  // Een bericht met een onbekende status wordt overgeslagen: hem als 'sent' tonen
  // zou een mislukking verbergen, en dat is de ene fout die dit scherm niet mag maken.
  if (id === '' || !isOneOf(status, MESSAGE_STATUSES) || !isOneOf(recipient, RECIPIENTS)) {
    return null
  }
  const sentAt = value['sentAt']
  return {
    id,
    status,
    recipient,
    stayId: typeof value['stayId'] === 'string' ? value['stayId'] : null,
    trigger: readText(value, 'trigger'),
    locale: readText(value, 'locale'),
    toAddress: readText(value, 'toAddress'),
    subject: readText(value, 'subject'),
    body: readText(value, 'body'),
    attempts: readCount(value, 'attempts'),
    lastError: readText(value, 'lastError'),
    scheduledFor: readText(value, 'scheduledFor'),
    sentAt: typeof sentAt === 'string' && sentAt !== '' ? sentAt : null,
  }
}

function readAutomation(value: unknown): AutomationRule | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const trigger = value['trigger']
  const recipient = value['recipient']
  if (id === '' || !isOneOf(trigger, AUTOMATION_TRIGGERS) || !isOneOf(recipient, RECIPIENTS)) {
    return null
  }
  return {
    id,
    trigger,
    recipient,
    templateKey: readText(value, 'templateKey'),
    offsetDays: readCount(value, 'offsetDays'),
    sendAtHour: readCount(value, 'sendAtHour'),
    enabled: readFlag(value, 'enabled'),
  }
}

function readTemplate(value: unknown): MessageTemplate | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const templateKey = readText(value, 'templateKey')
  if (id === '' || templateKey === '') {
    return null
  }
  return {
    id,
    templateKey,
    locale: readText(value, 'locale'),
    subject: readText(value, 'subject'),
    body: readText(value, 'body'),
    isOverride: readFlag(value, 'isOverride'),
    placeholders: Array.isArray(value['placeholders'])
      ? value['placeholders'].filter((name): name is string => typeof name === 'string')
      : [],
  }
}

function readList<T>(key: string, read: (value: unknown) => T | null) {
  return (payload: unknown): T[] | null => {
    if (!isRecord(payload) || !Array.isArray(payload[key])) {
      return null
    }
    const items: T[] = []
    for (const entry of payload[key]) {
      const item = read(entry)
      if (item !== null) {
        items.push(item)
      }
    }
    return items
  }
}

export async function listMessages(
  residenceId: string,
  status: MessageStatus | null,
  options: RequestOptions = {},
): Promise<MessagingResult<readonly LoggedMessage[]>> {
  const query = status === null ? '' : `?status=${encodeURIComponent(status)}`
  return call(
    'GET',
    `/residences/${encodeURIComponent(residenceId)}/messages${query}`,
    undefined,
    readList('messages', readMessage),
    options,
  )
}

export async function listAutomations(
  residenceId: string,
  options: RequestOptions = {},
): Promise<MessagingResult<readonly AutomationRule[]>> {
  return call(
    'GET',
    `/residences/${encodeURIComponent(residenceId)}/automations`,
    undefined,
    readList('automations', readAutomation),
    options,
  )
}

export async function saveAutomation(
  residenceId: string,
  input: {
    readonly trigger: AutomationTrigger
    readonly recipient: Recipient
    readonly offsetDays: number
    readonly sendAtHour: number
    readonly enabled: boolean
  },
): Promise<MessagingResult<readonly AutomationRule[]>> {
  return call(
    'PUT',
    `/residences/${encodeURIComponent(residenceId)}/automations`,
    input,
    readList('automations', readAutomation),
  )
}

export async function listTemplates(
  options: RequestOptions = {},
): Promise<MessagingResult<readonly MessageTemplate[]>> {
  return call('GET', '/message-templates', undefined, readList('templates', readTemplate), options)
}

export async function saveTemplate(input: {
  readonly templateKey: string
  readonly locale: string
  readonly subject: string
  readonly body: string
}): Promise<MessagingResult<readonly MessageTemplate[]>> {
  return call('PUT', '/message-templates', input, readList('templates', readTemplate))
}

export async function resetTemplate(
  templateKey: string,
  locale: string,
): Promise<MessagingResult<readonly MessageTemplate[]>> {
  return call(
    'DELETE',
    `/message-templates/${encodeURIComponent(templateKey)}/${encodeURIComponent(locale)}`,
    undefined,
    readList('templates', readTemplate),
  )
}

/** Vertaalsleutel bij een mislukte aanroep. */
export function messagingErrorMessageKey(failure: MessagingFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.messages.error.rejected'
    case 'not_found':
      return 'dashboard.messages.error.notFound'
    case 'forbidden':
      return 'dashboard.messages.error.forbidden'
    default:
      return 'dashboard.messages.error.unavailable'
  }
}
